/* Built In Imports */
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useState } from 'react';

/* External Imports */
import { Box } from '@chakra-ui/react';
import Masonry from 'react-masonry-css';

/* Internal Imports */
/* Components */
import QuoteCard from '@components/Card/QuoteCard';
import WisdomBanner from '@components/HeaderBanner/WisdomBanner';
import Breadcrum from '@components/Navigation/Breadcrum';
import HeadComponent from '@components/Utility/HeadComponent';
import NoData from '@components/Utility/NoData';
import config from '@config';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@components/Utility/Shared/SharedService';
import {
  fetchFooterData, fetchHeaderMenuData, fetchHeaderTopMenuData
} from 'services/commonService';
import {
  fetchRelatedQuotes,
  getWisdomLandingPaths
} from 'services/wisdomService';

/**
 * Renders the Quotes Page
 *
 * @function
 * @param footerData.headerMenuData
 * @param {object} footerData - data
 * @param {string} language - Current User Language
 * @param {object} headerMenuData - Header menu Data
 * @param footerData.region
 * @param footerData.language
 * @param footerData.pageDataProps
 * @param footerData.footerData
 * @param footerData.type
 * @param {string} region - Country
 * @returns {ReactElement} Quotes Page
 */
export default function Page({
  region,
  language,
  pageDataProps
}) {
  // const [currentUserRegion, setCurrentUserRegion] = useState(region);
  const [pageData, setPageData] = useState(pageDataProps);
  const router = useRouter();
  const { page } = router.query;
  const authContext = useContext(AuthContext);

  const breakpointColumnsObj = {
    default: 3,
    1100: 3,
    980: 2,
    768: 2,
    736: 1,
  };

  const getRelatedQuotes = useCallback(async () => {
    const pageData = await fetchRelatedQuotes(region, language, page);
    setPageData(pageData);
  }, [language, page, region]);

  useEffect(() => {
    // const currentRegion = config.locales.find(
    //   locale => locale.region_sf === region
    // ).region;
    authContext.RegionAndLang(region, language);
    // setCurrentUserRegion(currentRegion);
    // const currentUrl = window.location.hash;
  }, [region]);

  useEffect(() => {
    getRelatedQuotes();
  }, [page, getRelatedQuotes]);

  return pageData?.posts?.cards ? (
    <Box pos="relative" float="left" w="100%">
      {pageData._seoMetaTags && pageData.availableLocales ? (
        <HeadComponent
          data={pageData._seoMetaTags}
          availableLocals={pageData.availableLocales}
        >
          {' '}
        </HeadComponent>
      ) : null}
      <Box>
        <Breadcrum
          page={pageData.breadcrumbs}
          alsoin={pageData.alsoin}
          url={pageData.url}
          region={region}
          lang={language}
          pageConfig={pageData?.pageConfig}
        />
      </Box>
      {/* wisdom banner section */}
      <Box
        pos="relative"
        float="left"
        w="100%"
        p={{ base: '0px 10px', sm: '0px 20px', md: 'unset' }}
      >
        <WisdomBanner title={pageData.title} smallSize="true" />
      </Box>

      {/* filter section */}

      {/* view content section */}
      <Box
        pos="relative"
        w="100%"
        float="left"
        p={{ base: '0px 10px', sm: '0px 20px', md: 'unset' }}
      >
        <Box maxW="1170px" m="0 auto" p="20px 0" mt="2vw">
          {/* <ResponsiveMasonry
            columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}
          > */}
          <Masonry breakpointCols={breakpointColumnsObj}>
            {pageData?.posts?.cards?.length > 0 ? (
              pageData?.posts?.cards?.map((post, i) => {
                if (post.contentType === 'quotes') {
                  return (
                    <QuoteCard key={post.uuid} {...post} lang={language} />
                  );
                }
              })
            ) : (
              <NoData />
            )}
          </Masonry>
          {/* </ResponsiveMasonry> */}
        </Box>
      </Box>
    </Box>
  ) : (
    ''
  );
}

export const getStaticPaths = async () => {
  const wisdomLandingPaths = config.USE_LOCAL_URL
    ? [
      '/en/quotes/quotes-about-buddha',
      '/en/quotes/quotes-about-family',
      '/en/quotes/quotes-god',
      '/en/quotes/quotes-yoga',
      '/en/quotes/covid-tips-ac',
      `/en/quotes/quotes-inspirational`
    ]
    : await getWisdomLandingPaths('L1', '/quotes/');
  return {
    paths: wisdomLandingPaths,
    fallback: false
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, page },
  req,
}) => {
  const language = region;

  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale('', language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale('', language),
    'iso_non_regional_menu'
  );
  const footerData = await fetchFooterData(
    getLocale('', language),
    'isofooter'
  );
  // Canonical URL's doesn't populate in DOM if we comment this.
  const pageDataProps = await fetchRelatedQuotes('', language, page, 'wisdom', 'quotes', { preview, data: previewData });

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: '',
      language: language,
      pageDataProps: pageDataProps,
      footerData: footerData?.data || null,
      page: 'quotes',
      isPreview: preview ? true : false
    }
  };
};
